/* =========================================================================
 * OWNING AN ASSET — the one path from "a file or a picture somewhere" to
 * "a file SR hosts in HubSpot File Manager".
 *
 * THE RULE (owned-assets): every visual asset a build references must be owned
 * and hosted by SR, because a link to somebody else's server is a dead link
 * waiting to happen. A Canva thumbnail URL is the sharpest version of that
 * problem: it is a SIGNED, EXPIRING link, so a build that stored one is
 * carrying a reference that stops resolving on its own schedule.
 *
 * ⚠️ THE EXISTING UPLOAD CALLS WERE BROKEN, AND SILENTLY.
 *
 * `api.artworkUpload` is documented as multipart and three call sites built a
 * FormData for it — the crest picker, the concept crest and the Canva folder
 * picker. But `request()` only sends a body as-is when `rawBody` is set, and
 * `artworkUpload` never set it, so every one of those posted the string "{}".
 * The live route (sr-portal-backend server.js, commit 70178c5) has no multipart
 * parser at all and reads `{ name, kind, dataUrl }` off `express.json`, so it
 * answered 400 invalid_payload every time. Verified in the SHIPPED bundle, not
 * just in source: index-Ba4tISbR.js posts `body:e` with no rawBody.
 *
 * Each of those call sites then fell back to "kept as a manual reference",
 * which reads like a Canva outage rather than a request the server could never
 * have accepted. So the documented safety net for the whole Canva dependency
 * has never once worked.
 *
 * This module is the single correct encoder, in the shape the route actually
 * takes, so the four call sites cannot drift from it or from each other again.
 * ========================================================================= */
import { api } from '../../lib/api.js'

/* What the route accepts (UPLOAD_TYPES + UPLOAD_MAX_BYTES on the backend).
 * Mirrored here so a file is refused with a reason BEFORE a round trip, and
 * deliberately no wider than the server's list: refusing something the server
 * would have taken costs a retry, sending something it will not take costs a
 * confusing 400. */
export const OWNED_TYPES = Object.freeze(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'])
export const OWNED_MAX_BYTES = 6 * 1024 * 1024

/* KIND decides which sentence a rep is shown, and the two must never blend.
 *   policy    — SR or File Manager will not take this, and trying again is
 *               pointless. The rep is told what to do instead.
 *   transport — the storage write failed. Nothing is wrong with the design;
 *               the right advice is "try again", never "go export a PNG".
 *   config    — a deployment is missing a variable. Not a rejected file. */
export class OwnAssetError extends Error {
  constructor(message, { cause = null, status = null, correlationId = null, kind = 'policy' } = {}) {
    super(message)
    this.name = 'OwnAssetError'
    this.cause = cause
    this.status = status
    this.correlationId = correlationId
    this.kind = kind
  }
}

/* THE RETRY BUDGET. HubSpot's File Manager write is a Kafka produce that fails
 * under load AFTER its own internal retries ("after all attempts"), and SR was
 * making exactly one call into it. Four attempts, waiting 1s, 2s then 4s, so
 * seven seconds of backoff before a rep is told anything failed. */
export const RETRY_DELAYS = Object.freeze([1000, 2000, 4000])
export const MAX_ATTEMPTS = RETRY_DELAYS.length + 1

/* A failure a retry could plausibly fix. A 400/413/415 is the file being wrong
 * and it will be wrong again; a 503 not_configured is a missing env var. */
const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504])
const TERMINAL_CODES = new Set(['legacy_artwork_upload_held', 'not_configured', 'too_large', 'unsupported_type', 'invalid_payload', 'undecodable'])

function isRetryable(err) {
  if (TERMINAL_CODES.has(err?.body?.error || '')) return false
  const status = Number(err?.status || 0)
  if (!status) return true                 // no response at all: a network blip
  return RETRYABLE_STATUS.has(status)
}

/** A short reference a rep can quote and a server log can be grepped for. */
export function newCorrelationId() {
  const bytes = new Uint8Array(4)
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/* ⚠️ THE VENDOR'S TEXT STOPS HERE, AND THIS IS THE WHOLE POINT OF THIS FUNCTION.
 *
 * `api.js#request` promotes the backend's `detail` field onto ApiError.message,
 * and `/api/artwork/upload` forwards HubSpot's raw exception body in `detail`.
 * That is the exact wire that put
 * `com.hubspot.kafka.clients.producer.response.ProduceFailedException ... topic
 * 'file-manager-model-create-update-delete-topic'` in front of a rep.
 *
 * The exception is kept on `.cause` for the console and for the server log. What
 * leaves this module is a classified error carrying a sentence and a reference. */
function classify(err, correlationId) {
  if (err instanceof OwnAssetError) {
    if (!err.correlationId) err.correlationId = correlationId
    return err
  }
  const code = err?.body?.error || ''
  if (code === 'legacy_artwork_upload_held') return new OwnAssetError('Legacy shared uploads are on hold. Use the artwork job’s Proof files or Artwork Engine import for private, version-bound files. No file was uploaded.', { correlationId, kind:'held', cause:err })
  if (code === 'not_configured') {
    return new OwnAssetError(
      'File Manager is not configured on this deployment, so nothing can be stored yet.',
      { cause: err, status: err?.status, correlationId, kind: 'config' })
  }
  if (code === 'too_large' || code === 'unsupported_type') {
    return new OwnAssetError(err?.message || 'File Manager will not take that file.',
      { cause: err, status: err?.status, correlationId, kind: 'policy' })
  }
  return new OwnAssetError('HubSpot file storage did not accept it.',
    { cause: err, status: err?.status, correlationId, kind: 'transport' })
}

/**
 * A Blob/File as the base64 data URL the route reads.
 *
 * Deliberately NOT FileReader. FileReader exists only in a browser, so a
 * FileReader-based encoder cannot be driven by a test outside one, and this is
 * the exact seam where the last defect lived: the body was the wrong shape and
 * nothing could exercise it. `arrayBuffer()` plus base64 runs in both, so the
 * bytes that would leave the browser are the bytes a harness can assert on.
 */
export async function blobToDataUrl(blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  let b64
  if (typeof btoa === 'function') {
    // Chunked: String.fromCharCode(...) on a whole megabyte blows the stack.
    let bin = ''
    const CHUNK = 0x8000
    for (let i = 0; i < bytes.length; i += CHUNK) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK))
    }
    b64 = btoa(bin)
  } else if (typeof globalThis.Buffer === 'function') {
    // Node, i.e. a harness driving this module outside a browser.
    b64 = globalThis.Buffer.from(bytes).toString('base64')
  } else {
    throw new OwnAssetError('This environment has no base64 encoder, so nothing could be uploaded.')
  }
  const type = String(blob.type || '').toLowerCase().split(';')[0] || 'application/octet-stream'
  return `data:${type};base64,${b64}`
}

function checkBlob(blob, what) {
  const type = String(blob?.type || '').toLowerCase().split(';')[0]
  if (!OWNED_TYPES.includes(type)) {
    throw new OwnAssetError(
      `${what} is ${type || 'of an unknown type'}, and File Manager takes ${OWNED_TYPES.join(', ')}.`,
    )
  }
  if (blob.size > OWNED_MAX_BYTES) {
    throw new OwnAssetError(
      `${what} is ${(blob.size / 1048576).toFixed(1)}MB and the limit is ${(OWNED_MAX_BYTES / 1048576).toFixed(0)}MB.`,
    )
  }
}

/**
 * POST the encoded asset and insist on an owned URL coming back.
 *
 * A 200 with no `url` is NOT an upload. Reporting it as one is how a ref with
 * no file behind it ends up on a build and passes the locked-layer check, which
 * is exactly the failure `ConceptCrest` already refuses on its own.
 */
export async function uploadOwned({ name, kind = 'element', dataUrl, companyId, onAttempt, correlationId }) {
  const ref = correlationId || newCorrelationId()
  let last = null, attempts = 0

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    attempts = attempt
    try {
      /* The id goes WITH the request so the server log and the sentence the rep
       * reads carry the same reference. Harmless to a backend that ignores it. */
      const res = await api.artworkUpload({
        name, kind, dataUrl, correlationId: ref, ...(companyId ? { companyId } : {}),
      })
      const url = res?.url || res?.file?.url || ''
      const fileId = res?.fileId || res?.file?.id || res?.id || ''
      if (!url) {
        throw new OwnAssetError('The upload came back without a file URL, so nothing is owned yet.',
          { correlationId: ref, kind: 'transport' })
      }
      return { fileId: fileId || undefined, url, name: res?.name || name, correlationId: ref, attempts: attempt }
    } catch (err) {
      if (err?.name === 'AbortError') throw err
      last = err
      if (!isRetryable(err) || attempt === MAX_ATTEMPTS) break
      const waitMs = RETRY_DELAYS[attempt - 1]
      /* The caller shows a "retrying" state. A silent seven-second wait reads
       * as a hang and the rep clicks again, which is a second upload. */
      onAttempt?.({ attempt, of: MAX_ATTEMPTS, waitMs, correlationId: ref })
      await new Promise((resolve) => setTimeout(resolve, waitMs))
    }
  }

  const error = classify(last, ref)
  /* The vendor exception is never rendered, but it must not be thrown away
   * either: until the backend writes its own log line this console entry is
   * the only durable-ish trace, and it is keyed by the same reference. */
  try {
    console.error(`[artwork-upload ${ref}] ${error.kind} failure after ${attempts} attempts`, last)
  } catch { /* console is not load-bearing */ }
  error.attempts = attempts
  throw error
}

/** A local file from the operator's device, owned in File Manager. */
export async function uploadOwnedFile(file, { kind = 'element', companyId, name, onAttempt } = {}) {
  if (!file) throw new OwnAssetError('No file was chosen.')
  checkBlob(file, 'That file')
  const dataUrl = await blobToDataUrl(file)
  return uploadOwned({ name: name || file.name, kind, dataUrl, companyId, onAttempt })
}

/**
 * COPY A REMOTE IMAGE INTO FILE MANAGER — the Canva picker's path.
 *
 * The bytes are fetched in the browser and re-posted to the upload route, so
 * what lands on the build is SR's own file. Nothing here ever returns the
 * source URL: a caller that cannot own the asset must attach nothing rather
 * than fall back to the link, which would reinstate the exact problem.
 *
 * `credentials: 'omit'` because a Canva thumbnail URL is already signed, and
 * sending credentials to a third party is neither needed nor wanted.
 */
export async function ingestUrlAsOwned(srcUrl, { name, kind = 'element', signal, onAttempt } = {}) {
  const url = String(srcUrl || '')
  /* ⚠️ A POLICY REFUSAL, AND IT IS DECIDED BEFORE ANYTHING IS ATTEMPTED.
   * There is no copyable image, so all we could put on the build is the Canva
   * link itself, and that link expires. `kind: 'canva-link'` is what lets the
   * UI say so up front instead of saying it after a failure it did not cause. */
  if (!/^https?:\/\//i.test(url)) {
    throw new OwnAssetError('That design offers no image to copy, so the only thing left to store would be the Canva link.',
      { kind: 'canva-link' })
  }
  let res
  try {
    res = await fetch(url, { credentials: 'omit', signal })
  } catch (err) {
    if (err?.name === 'AbortError') throw err
    /* A cross-origin refusal and a dead link look the same from here, so say
     * what we tried rather than guessing which it was. This is Canva's host
     * failing, not SR storage, so it is its own kind. */
    throw new OwnAssetError('The preview image could not be read from its host, so nothing was copied.',
      { cause: err, kind: 'source' })
  }
  if (!res.ok) {
    throw new OwnAssetError(`The preview image answered ${res.status}, so nothing was copied.`,
      { status: res.status, kind: 'source' })
  }
  const blob = await res.blob()
  checkBlob(blob, 'That preview image')
  const dataUrl = await blobToDataUrl(blob)
  return uploadOwned({ name, kind, dataUrl, onAttempt })
}
